import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowSquareOut, CaretDown, CheckSquare, Fingerprint, CurrencyDollar, Clock, Files, Info, FilePdf } from "@phosphor-icons/react";
import Seo from "@/components/Seo";
import LiveFire from "@/components/LiveFire";
import { GROUPS, PROGRAMS } from "@/data/resources";
import { downloadChecklist } from "@/lib/checklistPdf";

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="border border-white/10 bg-black/40 p-4">
      <div className="flex items-center gap-2 overline mb-2"><Icon size={13} weight="duotone" className="text-[#FFD300]" />{label}</div>
      <div className="font-heading text-base tracking-tight text-white">{value}</div>
    </div>
  );
}

function ProgramCard({ p, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <article id={p.id} className="border border-white/10 bg-[#0A0A0A] scroll-mt-32" data-testid={`program-${p.id}`}>
      <button onClick={() => setOpen(!open)} className="w-full text-left p-6 md:p-8 flex items-start gap-5 hover:bg-white/[0.02] transition-colors" data-testid={`program-toggle-${p.id}`} aria-expanded={open}>
        <div className="w-16 h-16 flex-shrink-0 flex items-center justify-center bg-white rounded-full overflow-hidden">
          {p.seal ? <img src={p.seal} alt={`${p.state} seal`} className="w-full h-full object-contain p-1" /> : <span className="font-heading text-2xl text-black">{p.state}</span>}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <span className="state-tag">{p.state}</span>
            <span className="code-badge">{p.code}</span>
          </div>
          <h3 className="font-heading text-2xl md:text-3xl tracking-tighter leading-tight">{p.title}</h3>
          <div className="text-neutral-400 text-sm mt-1 truncate">{p.agency.name}</div>
        </div>
        <CaretDown size={22} className={`text-[#FFD300] flex-shrink-0 mt-2 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
            <div className="px-6 md:px-8 pb-8 border-t border-white/10 pt-8 space-y-10">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
                <Stat icon={CurrencyDollar} label="Fee" value={p.summary.fee} />
                <Stat icon={Clock} label="Processing" value={p.summary.processing} />
                <Stat icon={CheckSquare} label="Valid for" value={p.summary.validity} />
                <Stat icon={Fingerprint} label="Fingerprints" value={p.summary.prints} />
              </div>

              <div className="flex flex-wrap gap-3">
                <button type="button" onClick={() => downloadChecklist(p)} className="btn-primary inline-flex" data-testid={`checklist-pdf-${p.id}`}><FilePdf size={16} weight="duotone" /> Download PDF checklist</button>
                <a href={p.portal.url} target="_blank" rel="noopener noreferrer" className="btn-secondary inline-flex" data-testid={`portal-link-${p.id}`}>{p.portal.label} <ArrowSquareOut size={14} /></a>
                <a href={p.agency.url} target="_blank" rel="noopener noreferrer" className="btn-ghost inline-flex" data-testid={`agency-link-${p.id}`}>Official agency page <ArrowSquareOut size={14} /></a>
                <Link to={p.course} className="btn-ghost inline-flex" data-testid={`course-link-${p.id}`}>View course <ArrowRight size={14} /></Link>
              </div>

              <div className="grid grid-cols-12 gap-10">
                <div className="col-span-12 lg:col-span-7">
                  <div className="overline mb-6">Step-by-step</div>
                  <ol className="space-y-6">
                    {p.steps.map((s, i) => (
                      <li key={s.t} className="flex gap-5" data-testid={`step-${p.id}-${i}`}>
                        <div className="font-mono text-xs text-[#FFD300] tracking-[0.2em] pt-1 flex-shrink-0">// {String(i + 1).padStart(2, "0")}</div>
                        <div>
                          <div className="font-heading text-lg tracking-tight mb-1">{s.t}</div>
                          <p className="text-neutral-400 text-sm leading-relaxed">{s.d}</p>
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>

                <div className="col-span-12 lg:col-span-5 space-y-8">
                  <div className="border-l-2 border-[#FFD300] pl-5">
                    <div className="overline mb-3 flex items-center gap-2"><Fingerprint size={14} weight="duotone" className="text-[#FFD300]" /> Fingerprints</div>
                    <p className="text-sm text-neutral-300 leading-relaxed mb-3">{p.fingerprints.how}</p>
                    <ul className="space-y-1.5 mb-3">
                      {p.fingerprints.details.map((d) => <li key={d} className="font-mono text-[11px] tracking-wide text-neutral-400">· {d}</li>)}
                    </ul>
                    <a href={p.fingerprints.url} target="_blank" rel="noopener noreferrer" className="font-mono text-[11px] tracking-[0.18em] uppercase text-[#FFD300] hover:underline inline-flex items-center gap-1">Fingerprint info <ArrowSquareOut size={11} /></a>
                  </div>

                  <div>
                    <div className="overline mb-3 flex items-center gap-2"><CurrencyDollar size={14} weight="duotone" className="text-[#FFD300]" /> Fees</div>
                    <table className="w-full text-sm">
                      <tbody>
                        {p.fees.map(([label, amt]) => (
                          <tr key={label} className="border-t border-white/10">
                            <td className="py-2 pr-3 text-neutral-400">{label}</td>
                            <td className="py-2 text-right text-white font-mono text-xs">{amt}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div>
                    <div className="overline mb-3 flex items-center gap-2"><Files size={14} weight="duotone" className="text-[#FFD300]" /> Bring / upload</div>
                    <ul className="space-y-2">
                      {p.documents.map((d) => (
                        <li key={d} className="flex items-start gap-2 text-sm text-neutral-300"><CheckSquare size={15} weight="duotone" className="text-[#FFD300] mt-0.5 flex-shrink-0" />{d}</li>
                      ))}
                    </ul>
                  </div>

                  {p.notes?.length > 0 && (
                    <div className="bg-black/50 border border-white/10 p-4">
                      <div className="overline mb-2 flex items-center gap-2"><Info size={14} weight="duotone" className="text-[#FFD300]" /> Good to know</div>
                      <ul className="space-y-1.5">
                        {p.notes.map((n) => <li key={n} className="text-xs text-neutral-400 leading-relaxed">{n}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </article>
  );
}

export default function Resources() {
  return (
    <div data-testid="resources-page">
      <Seo
        title="After Your Class — State Application Guides, Fingerprints & Fees"
        description="Finished a Secrecy Executive course? Step-by-step instructions, official links, fingerprinting details and fees for every permit and license we train for: MD, NJ, DC, VA, AZ, FL, UT wear & carry, Maryland Security Guard, Armed Security Officer and DC Special Police."
        path="/resources"
      />

      <section className="relative border-b border-white/10 pt-24 pb-14 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src="/media/range-b-landscape.jpg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/80 to-transparent" />
        </div>
        <div className="container-x relative">
          <div className="overline mb-4">// FILE 003 — AFTER YOUR CLASS</div>
          <h1 className="font-heading text-5xl md:text-7xl tracking-tighter leading-[0.9] max-w-4xl mb-6">
            You passed. <span className="text-[#FFD300]">Here's what to file next.</span>
          </h1>
          <p className="text-neutral-300 max-w-2xl text-base md:text-lg leading-relaxed">
            Every permit and license we train for, broken down by state — the exact portal, fingerprint codes,
            fees, documents and deadlines. Download the one-page PDF checklist for your fingerprinting appointment.
          </p>
        </div>
      </section>

      <nav className="sticky top-20 z-30 bg-black/90 backdrop-blur border-b border-white/10" data-testid="resources-nav">
        <div className="container-x flex gap-1 overflow-x-auto py-3">
          {GROUPS.map((g) => (
            <a key={g.id} href={`#${g.id}`} className="font-mono text-[11px] tracking-[0.18em] uppercase text-neutral-300 hover:text-[#FFD300] whitespace-nowrap px-4 py-2 border border-white/10 hover:border-[#FFD300]/50" data-testid={`resources-nav-${g.id}`}>
              {g.label}
            </a>
          ))}
        </div>
      </nav>

      {GROUPS.map((g, gi) => {
        const items = PROGRAMS.filter((p) => p.group === g.id);
        return (
          <section key={g.id} id={g.id} className={`section-pad scroll-mt-36 ${gi % 2 ? "bg-[#050505] border-y border-white/10" : ""}`} data-testid={`resources-group-${g.id}`}>
            <div className="container-x">
              <div className="mb-10">
                <div className="overline mb-3">// {String(gi + 1).padStart(3, "0")} — {g.label}</div>
                <h2 className="font-heading text-4xl md:text-5xl tracking-tighter leading-[0.95]">{g.subtitle}</h2>
              </div>
              <div className="space-y-4">
                {items.map((p, i) => <ProgramCard key={p.id} p={p} defaultOpen={i === 0} />)}
              </div>
            </div>
          </section>
        );
      })}

      <section className="section-pad">
        <div className="container-x border border-white/10 bg-[#0A0A0A] p-8 md:p-12 grid grid-cols-12 gap-8 items-center">
          <div className="col-span-12 lg:col-span-8">
            <div className="overline mb-3">// DISCLAIMER</div>
            <p className="text-neutral-400 text-sm leading-relaxed">
              Fees, forms and processing times are set by each agency and change without notice. We verify this page regularly,
              but always confirm against the official link before paying or mailing anything. Lost your certificate? Download it
              anytime from your student dashboard.
            </p>
          </div>
          <div className="col-span-12 lg:col-span-4 flex flex-wrap gap-3 lg:justify-end">
            <Link to="/dashboard" className="btn-secondary inline-flex" data-testid="resources-dashboard-cta">My certificates <ArrowRight size={14} /></Link>
            <Link to="/contact" className="btn-ghost inline-flex" data-testid="resources-contact-cta">Ask ops <ArrowRight size={14} /></Link>
          </div>
        </div>
      </section>

      <LiveFire code="004" />
    </div>
  );
}
